import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Copy, Lock, Plus, Eye, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { PUBLIC_FRONT_URL } from "../lib/publicFront";

/* ────────────────────────────────────────────────────────────────────
   /cc/admin · Checkpoint Charlie issuance dashboard
   Admin-only. Two panels:
     1. Issue: form to register a new doc_id + secret. Returns Share A
        as a base64 PNG the operator downloads / prints / embeds.
     2. List: recent templates with verify_count + last_verified_at.
   ──────────────────────────────────────────────────────────────────── */

export default function CheckpointAdmin() {
  const [secretText, setSecretText] = useState("");
  const [docId, setDocId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [err, setErr] = useState("");
  const [list, setList] = useState([]);
  const [listErr, setListErr] = useState("");
  const [copiedField, setCopiedField] = useState("");

  async function loadList() {
    try {
      const r = await api.get("/cc/templates");
      setList(r.data?.items || []);
      setListErr("");
    } catch (e) {
      setListErr(formatApiError(e));
    }
  }

  useEffect(() => { loadList(); }, []);

  async function issue(e) {
    e.preventDefault();
    if (!secretText) return;
    setBusy(true);
    setErr("");
    setIssued(null);
    try {
      const body = { secret_text: secretText };
      if (docId) body.doc_id = docId.toUpperCase();
      if (label) body.label = label;
      const r = await api.post("/cc/templates/register", body);
      setIssued(r.data);
      setSecretText("");
      setDocId("");
      setLabel("");
      loadList();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy(text, key) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(key);
      setTimeout(() => setCopiedField(""), 1200);
    } catch { /* clipboard not available */ }
  }

  function downloadPng(b64, prefix) {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${b64}`;
    a.download = `${prefix}-${issued.doc_id}.png`;
    a.click();
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <header className="bg-black text-white border-b border-black">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] inline-flex items-center gap-2" data-testid="cc-admin-back">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2">
            <Lock size={14} weight="bold" /> CURB · CHECKPOINT · ADMIN
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-neutral-500">Issuance</div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
          Mint a <span className="bg-[#FFCC00] px-1">share pair</span>.
        </h1>
        <p className="mt-3 text-sm text-neutral-700 max-w-xl leading-relaxed">
          Share A prints on the document. Share B stays on the server — you&apos;ll never see it again. Both halves are required to verify.
        </p>

        {/* ─── Section nav so admin can jump to mint factory + audit ─── */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="cc-admin-nav">
          <Link
            to="/cc/admin/mints"
            className="bg-black text-white px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
            data-testid="cc-admin-mints-link"
          >
            Mint sticker factory →
          </Link>
          <a
            href={`${PUBLIC_FRONT_URL}/cc/enterprise`}
            className="border-2 border-black px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
          >
            Enterprise demo
          </a>
          <a
            href={`${PUBLIC_FRONT_URL}/cc/postnet`}
            className="border-2 border-black px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
          >
            Postnet operator
          </a>
        </div>

        {/* ─── Issue form ─── */}
        <form onSubmit={issue} className="mt-8 border-2 border-black bg-neutral-50 p-6 space-y-4">
          <label className="block">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Secret to embed (max 16 chars)</div>
            <input
              type="text"
              value={secretText}
              onChange={(e) => setSecretText(e.target.value.slice(0, 16))}
              placeholder="e.g. APPROVED-2026"
              className="mt-2 w-full bg-white border-2 border-black px-3 py-2 font-mono"
              maxLength={16}
              required
              data-testid="cc-admin-secret"
            />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Doc ID (optional · auto if blank)</div>
              <input
                type="text"
                value={docId}
                onChange={(e) => setDocId(e.target.value.toUpperCase().slice(0, 16))}
                placeholder="auto"
                className="mt-2 w-full bg-white border-2 border-black px-3 py-2 font-mono uppercase"
                data-testid="cc-admin-docid"
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">Label (your note, max 120 chars)</div>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                placeholder="e.g. Branch letter · KPMG audit batch"
                className="mt-2 w-full bg-white border-2 border-black px-3 py-2"
                maxLength={120}
                data-testid="cc-admin-label"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={busy || !secretText}
            className="inline-flex items-center gap-2 bg-black text-[#FFCC00] px-5 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-40"
            data-testid="cc-admin-issue-btn"
          >
            <Plus size={14} weight="bold" /> {busy ? "Minting…" : "Mint share pair"}
          </button>

          {err && (
            <div className="border-2 border-red-600 bg-red-50 text-red-900 p-3 text-sm flex items-start gap-2" data-testid="cc-admin-error">
              <XCircle size={16} weight="bold" className="shrink-0" /> {err}
            </div>
          )}
        </form>

        {/* ─── Issued result ─── */}
        {issued && (
          <div className="mt-8 border-2 border-[#FFCC00] bg-black text-white p-6" data-testid="cc-admin-issued">
            <div className="flex items-center gap-2 text-[#FFCC00]">
              <CheckCircle size={20} weight="bold" />
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Share pair issued</div>
            </div>

            <div className="mt-4 grid sm:grid-cols-5 gap-6 items-start">
              <div className="sm:col-span-2 bg-white p-3 border-2 border-[#FFCC00]">
                <img
                  src={`data:image/png;base64,${issued.share_a_png_b64}`}
                  alt="Share A — print on document"
                  className="block w-full h-auto"
                  data-testid="cc-admin-share-a-img"
                />
                <div className="mt-2 font-mono text-[9px] tracking-[0.3em] uppercase text-neutral-700">
                  SHARE A · PRINTABLE
                </div>
              </div>

              <div className="sm:col-span-3 space-y-3 text-sm">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFCC00]">Doc ID</div>
                  <button
                    onClick={() => copy(issued.doc_id, "id")}
                    className="mt-1 inline-flex items-center gap-2 bg-white text-black px-3 py-1 font-mono text-lg"
                    data-testid="cc-admin-copy-docid"
                  >
                    {issued.doc_id} <Copy size={12} weight="bold" />
                    {copiedField === "id" && <span className="text-[10px] tracking-widest">COPIED</span>}
                  </button>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={() => downloadPng(issued.share_a_png_b64, "share-a")}
                    className="inline-flex items-center gap-2 bg-[#FFCC00] text-black px-3 py-2 font-mono text-[10px] tracking-[0.3em] uppercase"
                    data-testid="cc-admin-download-a"
                  >
                    <Download size={12} weight="bold" /> Share A PNG
                  </button>
                  <button
                    onClick={() => downloadPng(issued.share_b_png_b64, "share-b-backup")}
                    className="inline-flex items-center gap-2 border border-white/40 text-white/80 px-3 py-2 font-mono text-[10px] tracking-[0.3em] uppercase"
                    data-testid="cc-admin-download-b"
                  >
                    <Download size={12} weight="bold" /> Share B (backup only)
                  </button>
                  <a
                    href={`${PUBLIC_FRONT_URL}/cc/scan`}
                    className="inline-flex items-center gap-2 border border-[#FFCC00] text-[#FFCC00] px-3 py-2 font-mono text-[10px] tracking-[0.3em] uppercase"
                  >
                    Open scanner
                  </a>
                </div>

                <p className="text-xs text-white/60 leading-relaxed pt-2">
                  Share A → print on the document. Share B is also persisted in the database for verification. Download Share B only if you need an offline backup; the server already has it.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Recent list ─── */}
        <div className="mt-16">
          <div className="flex items-center gap-3">
            <Eye size={18} weight="bold" />
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Recent issuances</div>
          </div>

          {listErr && (
            <div className="mt-3 border-2 border-red-600 bg-red-50 text-red-900 p-3 text-sm">{listErr}</div>
          )}

          <div className="mt-4 overflow-x-auto border-2 border-black">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase">Doc ID</th>
                  <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase">Label</th>
                  <th className="text-right p-3 font-mono text-[10px] tracking-widest uppercase">Verifies</th>
                  <th className="text-right p-3 font-mono text-[10px] tracking-widest uppercase">Last verified</th>
                  <th className="text-right p-3 font-mono text-[10px] tracking-widest uppercase">Created</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-neutral-500 text-sm">No issuances yet.</td></tr>
                )}
                {list.map((r) => (
                  <tr key={r.doc_id} className="border-t border-neutral-200">
                    <td className="p-3 font-mono">{r.doc_id}</td>
                    <td className="p-3">{r.label || <span className="text-neutral-400">—</span>}</td>
                    <td className="p-3 text-right font-mono">{r.verify_count || 0}</td>
                    <td className="p-3 text-right font-mono text-xs">
                      {r.last_verified_at
                        ? new Date(r.last_verified_at).toLocaleString()
                        : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="p-3 text-right font-mono text-xs text-neutral-600">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
